import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Textarea,
  Flex,
  Spinner
} from '@chakra-ui/react';
import axios from 'axios';
import { CalendarIcon } from '@chakra-ui/icons';

const EditStrikeRate = ({ isOpen, onClose, selectedRetailer, selectedSupplier, onDataSaved }) => {
  const [weekEndingDates, setWeekEndingDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [newDate, setNewDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  // Fetch week ending dates on component mount
  useEffect(() => {
    if (isOpen) {
      fetchWeekEndingDates();
    }
  }, [isOpen, selectedRetailer]);

  const fetchWeekEndingDates = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/week-ending-date`, {
        retailer: selectedRetailer
      });
    console.log(response.data.data)
      if (response.data.data && Array.isArray(response.data.data)) {
        setWeekEndingDates(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedDate(response.data.data[0].week_ending_date || '');
        }
      } else {
        setWeekEndingDates([]);
      }
    } catch (error) {
      console.error('Error fetching week ending dates:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch week ending dates',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateWeekEndingDate = async () => {
    if (!selectedDate || !newDate) {
      toast({
        title: 'Error',
        description: 'Please select a week ending date and a new date',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/upd-week-ending-date`, {
        retailer: selectedRetailer,
        old_date: selectedDate,
        new_date: newDate
      });
      
      toast({
        title: 'Success',
        description: 'Week ending date updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh the dates
      fetchWeekEndingDates();
      setNewDate('');
      
      if (onDataSaved) onDataSaved();
    } catch (error) {
      console.error('Error updating week ending date:', error);
      toast({
        title: 'Error',
        description: 'Failed to update week ending date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Strike Rate Data</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Flex justify="center" align="center" minH="300px">
              <Spinner size="xl" />
            </Flex>
          ) : (
            <Tabs isFitted variant="enclosed">
              <TabList mb="1em">
                <Tab>Dates</Tab>
                <Tab>Other</Tab>
              </TabList>
              <TabPanels>
                {/* Dates Tab */}
                <TabPanel>
                  <VStack spacing={6} align="stretch">
                    <FormControl>
                      <FormLabel>Select Week Ending Date</FormLabel>
                      <Select 
                        placeholder="Select week ending date" 
                        value={selectedDate} 
                        onChange={(e) => setSelectedDate(e.target.value)}
                      >
                        {weekEndingDates.map((dateObj, index) => (
                          <option key={index} value={dateObj.week_ending_date}>
                            {dateObj.week_ending_date}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>New Date</FormLabel>
                      <HStack>
                        <Box flex="1">
                          <input
                            type="date"
                            value={newDate}
                            onChange={(e) => setNewDate(e.target.value)}
                            style={{
                              border: '1px solid #CBD5E0',
                              borderRadius: '0.375rem',
                              padding: '0.5rem',
                              width: '100%'
                            }}
                          />
                        </Box>
                        <CalendarIcon color="gray.500" />
                      </HStack>
                    </FormControl>

                    <Button 
                      colorScheme="blue" 
                      onClick={handleUpdateWeekEndingDate} 
                      isLoading={isSaving}
                      mt={2}
                    >
                      Update Date
                    </Button>
                  </VStack>
                </TabPanel>

                {/* Other Tab - Left intentionally blank */}
                <TabPanel>
                  <Box p={4} borderWidth="1px" borderRadius="md">
                    <Flex justify="center" align="center" minH="100px">
                      <Box as="span" color="gray.500">
                        Additional configuration options will be available here in future updates.
                      </Box>
                    </Flex>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditStrikeRate;
